/* eslint-disable no-console */
import type { Language } from '@/types';
import { storage } from '@/utils/helpers';
import { getTranslation, isValidLanguage } from '@/utils/translations';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ContactForm from '@/components/ContactForm';
import MobileMenu from '@/components/MobileMenu';
import Router from '@/utils/Router';
import PageRenderer from '@/utils/PageRenderer';
import { initReveal } from '@/effects/reveal';
import { initHero3D } from '@/effects/hero3d';
import { initSmoothScroll } from '@/effects/smoothScroll';
import { prefersReducedMotion } from '@/effects/motion';
import { initTilt } from '@/effects/tilt';
import { initPortfolioCarousel } from '@/components/PortfolioCarousel';
import { ScrollProgress } from '@/components/ScrollProgress';
import { initCursor } from '@/effects/cursor';
import { initHeroEntrance, scrambleIn } from '@/effects/heroEntrance';

/**
 * Main application class that orchestrates all components
 */
class App {
  private contactForm: ContactForm | null = null;
  private mobileMenu: MobileMenu | null = null;
  private scrollProgress: ScrollProgress | null = null;
  private router: Router;
  private pageRenderer: PageRenderer;
  private currentLanguage: Language = 'fr';
  private languageChangeHandler: (e: Event) => void;
  private disposeReveal: (() => void) | null = null;
  private hero3d: { destroy: () => void } | null = null;
  private scrollTopButton: HTMLButtonElement | null = null;
  private onScrollForScrollTop: (() => void) | null = null;
  private disposeCursor: (() => void) | null = null;
  private navScrollHandler: (() => void) | null = null;

  private readonly STORAGE_KEY = 'devmeup-language';

  constructor() {
    this.languageChangeHandler = (e: Event): void => {
      const customEvent = e as CustomEvent<Language>;
      this.handleLanguageChange(customEvent.detail);
    };

    // Initialize routing system
    this.router = new Router();
    this.pageRenderer = new PageRenderer();
    this.setupRoutes();

    // Check if this is a special route (like privacy policy)
    const path = window.location.pathname;
    console.log('Current path:', path);
    if (this.isSpecialRoute(path)) {
      console.log('Detected special route:', path);
      // Handle special routes immediately
      this.handleSpecialRoute(path);
      return;
    }

    // Initialize components first
    try {
      new LanguageSwitcher();
    } catch (e) {
      console.warn('LanguageSwitcher error', e);
    }
    try {
      this.contactForm = new ContactForm();
    } catch (e) {
      console.warn('ContactForm error', e);
    }
    try {
      this.mobileMenu = new MobileMenu();
    } catch (e) {
      console.warn('MobileMenu error', e);
    }
    try {
      this.scrollProgress = new ScrollProgress();
      this.scrollProgress.init();
    } catch (e) {
      console.warn('ScrollProgress error', e);
    }

    // Then initialize the app
    this.init();
  }

  private setupRoutes(): void {
    // Handle privacy policy routes
    this.router.addRoute('/app/:appName/:page', params => {
      if (params.page === 'privacy') {
        this.pageRenderer.renderPrivacyPolicy(params.appName);
      } else {
        this.pageRenderer.renderPage404();
      }
    });
  }

  private isSpecialRoute(path: string): boolean {
    // Check if the path matches patterns like /appname/privacy
    const specialRoutePattern = /^\/[^/]+\/(privacy|terms|about)$/;
    return specialRoutePattern.test(path);
  }

  private handleSpecialRoute(path: string): void {
    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2 && parts[1] === 'privacy' && parts[0]) {
      const appName = parts[0];
      this.pageRenderer.renderPrivacyPolicy(appName);
    } else {
      this.pageRenderer.renderPage404();
    }
  }

  private init(): void {
    this.loadSavedLanguage();
    this.initializeTranslations();

    // Hero entrance animation (runs once after translations are applied)
    try {
      initHeroEntrance();
      // Scramble effect on the hero title paragraph
      const heroTitle = document.querySelector<HTMLElement>('.hero-title');
      if (heroTitle) scrambleIn(heroTitle, 1.3);
    } catch (e) {
      console.warn('HeroEntrance error', e);
    }

    try {
      initPortfolioCarousel();
    } catch (e) {
      console.warn('Carousel error', e);
    }
    try {
      initTilt();
    } catch (e) {
      console.warn('Tilt error', e);
    }

    try {
      initSmoothScroll();
    } catch (e) {
      console.warn('Smooth Scroll error', e);
    }

    this.initializeAnimations();

    // Custom cursor (desktop only)
    try {
      this.disposeCursor = initCursor();
      document.documentElement.classList.add('has-custom-cursor');
    } catch (e) {
      console.warn('Cursor error', e);
    }

    // Nav glassmorphism on scroll
    this.initNavScroll();

    // Progressive enhancement: load 3D effect lazily.
    void this.initializeHero3D();
    this.initializeScrollToTop();
  }

  private initNavScroll(): void {
    const nav = document.querySelector('nav');
    if (!nav) return;

    let ticking = false;
    const onScroll = (): void => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        nav.classList.toggle('nav-scrolled', window.scrollY > 40);
        ticking = false;
      });
    };

    this.navScrollHandler = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  private loadSavedLanguage(): void {
    const saved = storage.get<string>(this.STORAGE_KEY);

    if (saved && isValidLanguage(saved)) {
      this.currentLanguage = saved as Language;
    } else {
      // Detect browser language
      const browserLang = navigator.language.slice(0, 2);
      this.currentLanguage = isValidLanguage(browserLang) ? (browserLang as Language) : 'fr';
    }
  }

  private initializeTranslations(): void {
    // Update all elements with data-translate attribute
    this.updateTranslations();

    // Update placeholders
    this.updatePlaceholders();

    // Update aria-label translations
    this.updateAriaLabels();

    // Update navigation active states
    this.updateNavigation();
  }

  private updateTranslations(): void {
    const elements = document.querySelectorAll('[data-translate]');

    elements.forEach(element => {
      const key = element.getAttribute('data-translate');
      if (key) {
        const translation = getTranslation(this.currentLanguage, key);

        // Handle HTML content vs text content
        if (translation.includes('<') && translation.includes('>')) {
          element.innerHTML = translation;
        } else {
          element.textContent = translation;
        }
      }
    });
  }

  private updatePlaceholders(): void {
    const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      '[data-translate-placeholder]'
    );

    inputs.forEach(input => {
      const key = input.getAttribute('data-translate-placeholder');
      if (key) {
        const placeholder = getTranslation(this.currentLanguage, key);
        input.placeholder = placeholder;
      }
    });
  }

  private updateAriaLabels(): void {
    const elements = document.querySelectorAll<HTMLElement>('[data-translate-aria]');

    elements.forEach(element => {
      const key = element.getAttribute('data-translate-aria');
      if (!key) return;
      element.setAttribute('aria-label', getTranslation(this.currentLanguage, key));
    });
  }

  private initializeScrollToTop(): void {
    this.scrollTopButton = document.getElementById('scroll-to-top') as HTMLButtonElement | null;
    if (!this.scrollTopButton) return;

    const toggleVisibility = (): void => {
      const shouldShow = window.scrollY > 520;
      this.scrollTopButton?.classList.toggle('is-visible', shouldShow);
    };

    let scheduled = false;
    const onScroll = (): void => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        toggleVisibility();
      });
    };

    this.onScrollForScrollTop = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    toggleVisibility();

    this.scrollTopButton.addEventListener('click', () => {
      if (prefersReducedMotion()) {
        window.scrollTo(0, 0);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  private updateNavigation(): void {
    // Update language switcher flags
    const flags = document.querySelectorAll('.language-flag');
    flags.forEach(flag => {
      const lang = flag.getAttribute('data-lang') as Language;
      flag.classList.toggle('active', lang === this.currentLanguage);
    });

    // Update mobile menu
    this.mobileMenu?.updateLanguage(this.currentLanguage);

    // Update contact form
    this.contactForm?.updateLanguage(this.currentLanguage);
  }

  private handleLanguageChange(language: Language): void {
    this.currentLanguage = language;
    storage.set(this.STORAGE_KEY, language);

    this.initializeTranslations();
  }

  private handleResize(): void {
    // Update any responsive components
    this.debounce(() => {
      // Add any resize-specific logic here
    }, 100)();
  }

  private initializeAnimations(): void {
    // Modern scroll reveal (respects prefers-reduced-motion)
    this.disposeReveal?.();
    this.disposeReveal = initReveal();
  }

  private async initializeHero3D(): Promise<void> {
    // Lightweight hero 3D scene (disabled on small screens / reduced motion)
    this.hero3d?.destroy();
    this.hero3d = await initHero3D();
  }

  private debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  public destroy(): void {
    // Clean up all components
    this.mobileMenu?.destroy();
    this.disposeReveal?.();
    this.disposeReveal = null;
    this.hero3d?.destroy();
    this.hero3d = null;
    this.disposeCursor?.();
    this.disposeCursor = null;

    // Remove event listeners
    document.removeEventListener('languageChange', this.languageChangeHandler);
    window.removeEventListener('resize', this.handleResize.bind(this));

    if (this.navScrollHandler) {
      window.removeEventListener('scroll', this.navScrollHandler);
      this.navScrollHandler = null;
    }

    if (this.onScrollForScrollTop) {
      window.removeEventListener('scroll', this.onScrollForScrollTop);
      this.onScrollForScrollTop = null;
    }
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new App();
  });
} else {
  new App();
}

// Export for potential external use
export default App;
